<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from "vue-i18n";

import MainTitleComponent from "@/components/ui/MainTitleComponent.vue";
import validators from "@/utils/validators.ts";
import { useAuthStore } from "@/stores/authStore.ts";
import FeesService from "@/services/fees/feesService.ts";
import { displayToast } from "@/utils/toastUtils.ts";

import type { ValidationRule } from "@/types/utils/validators.type.ts";
import type {
  PlatformFeesRequest,
  PlatformFeesResponse
} from "@/types/services/fees/platformFees.type.ts";
import { PeriodicityKey } from '@/enums/periodicity_enum.ts';

const { t } = useI18n();
const authStore = useAuthStore()

const percentageRules = ref<ValidationRule[]>([...validators.mandatePercentageRules])

const loading = ref<boolean>(false);

//--- Platform fee form ---//
const form = reactive<{
  subscriptionFee: number
  managementFee: number
  managementPeriodicity: PeriodicityKey
  applyToExistingMandates: boolean
}>({
  subscriptionFee: 0,
  managementFee: 0,
  managementPeriodicity: PeriodicityKey.MONTHLY,
  applyToExistingMandates: false,
});

const periodicityOptions = computed(() => [
  { title: t('common.periodicity.monthly'), value: PeriodicityKey.MONTHLY },
  { title: t('common.periodicity.quarterly'), value: PeriodicityKey.QUARTERLY },
  { title: t('common.periodicity.semiannual'), value: PeriodicityKey.SEMIANNUAL },
  { title: t('common.periodicity.yearly'), value: PeriodicityKey.YEARLY },
]);

function trimTrailingZeros(value: number | string): number {
  if (value === null || value === undefined) return 0;

  const str = String(value)
    .replace(/(\.\d*?[1-9])0+$/g, "$1")
    .replace(/\.0+$/, "");
  return parseFloat(str);
}

async function getPlatformFees(): Promise<void> {
  loading.value = true;

  try {
    const response: PlatformFeesResponse = await FeesService.getPlatformFees();

    if (response) {
      Object.assign(form, {
        subscriptionFee: trimTrailingZeros(response.subscription_fee),
        managementFee: trimTrailingZeros(response.management_fee),
        managementPeriodicity: response.management_periodicity,
      })
    }
  } finally {
    loading.value = false;
  }
}

onMounted(getPlatformFees);

function hasPermission(permission: string[]): boolean {
  return authStore.hasPermission(permission);
}

async function updatePlatformFee(): Promise<void> {
  loading.value = true;

  const platformFeesData: PlatformFeesRequest = {
    subscriptionFee: form.subscriptionFee,
    managementFee: form.managementFee,
    managementPeriodicity: form.managementPeriodicity,
    applyToExistingMandates: form.applyToExistingMandates,
  }

  try {
    const response: PlatformFeesResponse = await FeesService.updatePlatformFees(platformFeesData)

    if (response) {
      Object.assign(form, {
        subscriptionFee: trimTrailingZeros(response.subscription_fee),
        managementFee: trimTrailingZeros(response.management_fee),
        managementPeriodicity: response.management_periodicity,
        applyToExistingMandates: false,
      })
      displayToast(t('views.adminFeesManagement.updateFeesSuccess'), 'success');
    }
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <!-- Main title -->
  <MainTitleComponent icon="PhReceipt">
    {{ t("views.adminFeesManagement.title") }}
  </MainTitleComponent>

  <!-- Subscription Fee -->
  <v-row>
    <v-col class="d-flex align-center" cols="12">
      <v-icon class="mr-2" color="primary" icon="PhCoins"/>
      <h2 class="subtitle">{{ t("views.adminFeesManagement.subtitle1") }}</h2>
    </v-col>
    <v-col cols="12" md="6">
      <v-text-field
        v-model.number="form.subscriptionFee"
        :disabled="!hasPermission(['EDIT_PLATFORM_FEE'])"
        :label="t('views.adminFeesManagement.inputs.subscriptionFee.label')"
        :min="0"
        :rules="percentageRules"
        density="compact"
        hide-details="auto"
        suffix="%"
        type="number"
      />
    </v-col>
  </v-row>

  <!-- Management Fee -->
  <v-row>
    <v-col class="d-flex align-center" cols="12">
      <v-icon class="mr-2" color="primary" icon="PhRepeat"/>
      <h2 class="subtitle">{{ t("views.adminFeesManagement.subtitle2") }}</h2>
    </v-col>
    <v-col cols="12" md="6">
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="form.managementFee"
            :disabled="!hasPermission(['EDIT_PLATFORM_FEE'])"
            :label="t('views.adminFeesManagement.inputs.managementFee.label')"
            :min="0"
            :rules="percentageRules"
            density="compact"
            hide-details="auto"
            suffix="%"
            type="number"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="form.managementPeriodicity"
            :disabled="!hasPermission(['EDIT_PLATFORM_FEE'])"
            :items="periodicityOptions"
            item-title="title"
            item-value="value"
            :label="t('views.adminFeesManagement.inputs.managementPeriodicity.label')"
            hide-details="auto"
            density="compact"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-checkbox
            v-model="form.applyToExistingMandates"
            :disabled="!hasPermission(['EDIT_PLATFORM_FEE'])"
            :label="t('views.adminFeesManagement.inputs.applyToExistingMandates.label')"
            hide-details="auto"
            density="compact"
          />
        </v-col>
      </v-row>
    </v-col>
  </v-row>

  <!-- Save button -->
  <v-row v-if="hasPermission(['EDIT_PLATFORM_FEE'])">
    <v-col cols="12" md="6" class="d-flex justify-end">
      <v-btn
        :disabled="loading"
        :loading="loading"
        :text="t('views.adminFeesManagement.buttons.update')"
        class="w-100 w-sm-auto"
        type="submit"
        @click="updatePlatformFee"
      >
        <template #prepend>
          <v-icon icon="PhCheck"/>
        </template>
      </v-btn>
    </v-col>
  </v-row>
</template>
